import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FilterModel } from 'src/models';
import {
    LayoutAddModel,
    LayoutEditModel,
    LayoutApiAddModel,
    LayoutApiEditModel,
    LayoutChangeStatusModel,
    LayoutChangeGroupModel,
    LayoutTemplateModel,
    LayoutTemplateSectionModel,
    LayoutForEditDto,
    LayoutListItemDto,
    mapZoneStatesToSections
} from '../models';

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    private apiEndPoint = environment.apiBaseUrl + 'api/layout/';

    constructor(private http: HttpClient) { }

    // ── Templates ────────────────────────────────────────────────────────

    getTemplates(): Observable<LayoutTemplateModel[]> {
        return this.http.get<LayoutTemplateModel[]>(this.apiEndPoint + 'templates');
    }

    getTemplateSections(templateId: number): Observable<LayoutTemplateSectionModel[]> {
        return this.http.get<LayoutTemplateSectionModel[]>(this.apiEndPoint + `templates/${templateId}/sections`);
    }

    // ── CRUD ─────────────────────────────────────────────────────────────

    add(model: LayoutAddModel): Observable<any> {
        const body = this.buildApiAddModel(model);
        return this.http.post(this.apiEndPoint + 'add', body);
    }

    getForEdit(id: string): Observable<LayoutForEditDto> {
        return this.http.get<LayoutForEditDto>(this.apiEndPoint + `get-for-edit/${id}`);
    }

    edit(model: LayoutEditModel): Observable<any> {
        const body = this.buildApiEditModel(model);
        return this.http.post(this.apiEndPoint + 'edit', body);
    }

    delete(id: string): Observable<any> {
        return this.http.post(this.apiEndPoint + `delete/${id}`, {});
    }

    deleteBulk(ids: number[]): Observable<any> {
        return this.http.post(this.apiEndPoint + `delete-bulk`, ids);
    }

    duplicate(id: string): Observable<any> {
        return this.http.post(this.apiEndPoint + `duplicate/${id}`, {});
    }

    changeStatus(model: LayoutChangeStatusModel): Observable<any> {
        return this.http.post(this.apiEndPoint + 'change-status', model);
    }

    changeGroup(model: LayoutChangeGroupModel): Observable<any> {
        return this.http.post(this.apiEndPoint + 'change-group', model);
    }

    // ── Lists & Paging ───────────────────────────────────────────────────

    getPagedList(params: any): Observable<any> {
        if (!params.groupId || params.groupId === -1) {
            params.groupId = null;
        }
        return this.http.get(this.apiEndPoint + 'paged-list', { params });
    }

    getPagedResult(params: any): Observable<any> {
        return this.http.get(this.apiEndPoint + 'paged-result', { params });
    }

    exists(filterModel: FilterModel, folderId?: number): Observable<boolean> {
        const groupId  = (!filterModel.groupId || filterModel.groupId === -1) ? null : filterModel.groupId;
        const folder   = folderId ?? null;
        return this.http.get<boolean>(
            `${this.apiEndPoint}exists/${groupId}/${folder}?mineOnly=${filterModel.mineOnly}`
        );
    }

    getSelectListItems(groupId?: number): Observable<any> {
        const gId = (!groupId || groupId === -1) ? null : groupId;
        return this.http.get(this.apiEndPoint + `get-select-list-items/${gId}`);
    }

    getSelectListItemsForGroupEdit(groupId: number): Observable<any> {
        return this.http.get(this.apiEndPoint + `get-select-list-items-for-group-edit/${groupId}`);
    }

    // ── Folders (listing-page folder view) ───────────────────────────────

    folderExists(groupId?: number, mineOnly?: boolean): Observable<any> {
        if (groupId == undefined || groupId === -1) {
            groupId = null;
        }
        if (mineOnly == undefined) {
            mineOnly = null;
        }
        return this.http.get(`${this.apiEndPoint}has-folder/${groupId}?mineOnly=${mineOnly}`);
    }

    getFolderList(filterKey: string, groupId?: number, mineOnly?: boolean): Observable<any> {
        if (groupId == undefined || groupId === -1) {
            groupId = null;
        }
        if (mineOnly == undefined) {
            mineOnly = null;
        }
        return this.http.get(`${this.apiEndPoint}folders/${groupId}?mineOnly=${mineOnly}&filterKey=${filterKey}`);
    }

    getLastSaved(groupId?: number): Observable<LayoutListItemDto> {
        const gId = (!groupId || groupId === -1) ? null : groupId;
        return this.http.get<LayoutListItemDto>(this.apiEndPoint + `last-saved/${gId}`);
    }

    getDefaultTitle(): Observable<string> {
        return this.http.get(this.apiEndPoint + 'get-default-title', { responseType: 'text' });
    }

    // ── Content select lists (for zone pickers) ──────────────────────────

    getPlaylistSelectList(groupId?: number): Observable<any> {
        const gId = (!groupId || groupId === -1) ? null : groupId;
        return this.http.get(environment.apiBaseUrl + `api/displayList/get-select-list-items/${gId}`);
    }

    getScheduleSelectList(groupId?: number): Observable<any> {
        const gId = (!groupId || groupId === -1) ? null : groupId;
        return this.http.get(environment.apiBaseUrl + `api/scheduler/select-list/${gId}`);
    }

    getMediaSelectList(): Observable<any> {
        return this.http.get(environment.apiBaseUrl + 'api/media/select-list-item');
    }

    getBroadcastSelectList(groupId?: number): Observable<any> {
        const gId = (!groupId || groupId === -1) ? null : groupId;
        return this.http.get(environment.apiBaseUrl + `api/broadcast/select-list/${gId}`);
    }

    // ── Private mapping helpers ──────────────────────────────────────────

    private formatRefreshTime(time: Date): string {
        if (!time) { return '03:00:00'; }
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(time.getHours())}:${pad(time.getMinutes())}:00`;
    }

    private buildLayoutProperties(model: LayoutAddModel): string {
        return JSON.stringify({
            orientation: model.type,
            showZoneBorders: model.showZoneBorders !== false,
        });
    }

    private buildApiAddModel(model: LayoutAddModel): LayoutApiAddModel {
        const body = new LayoutApiAddModel();
        body.name             = model.name;
        body.layoutTemplateId = model.layoutTemplateId;
        body.groupId          = (!model.groupId || model.groupId === -1) ? null : model.groupId;
        body.folderId         = model.folderId ?? null;
        body.layoutProperties = this.buildLayoutProperties(model);
        body.refreshTime          = this.formatRefreshTime(model.refreshTime);
        body.refreshIntervalHours = model.refreshIntervalHours ?? 24;
        body.sections         = mapZoneStatesToSections(model.zoneStates);
        body.displayIds       = model.displayIds ?? [];
        return body;
    }

    private buildApiEditModel(model: LayoutEditModel): LayoutApiEditModel {
        const body    = new LayoutApiEditModel();
        body.id       = model.id ? parseInt(model.id, 10) : null;
        body.name             = model.name;
        body.layoutTemplateId = model.layoutTemplateId;
        body.groupId          = (!model.groupId || model.groupId === -1) ? null : model.groupId;
        body.folderId         = model.folderId ?? null;
        body.layoutProperties = this.buildLayoutProperties(model);
        body.refreshTime          = this.formatRefreshTime(model.refreshTime);
        body.refreshIntervalHours = model.refreshIntervalHours ?? 24;
        body.sections         = mapZoneStatesToSections(model.zoneStates);
        body.displayIds       = model.displayIds ?? [];
        return body;
    }
}
